import React from 'react';
import { useTranslation } from 'react-i18next';
import { selectLabel, uiLang, useVocabulary, vocabLabels } from '../../kinds';
import { pathLabel } from '../../locations';
import { Chip } from '../../ui';
import { NO_PLACE, YEAR } from './filters';
import { tagKey } from './fold';

/** The label of one value of a field, as its facet shows it. */
function ValueChip({ field, fieldKey, value, facet, onRemove }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const vocabulary = useVocabulary(field && field.type === 'vocab' ? field.vocabulary : null);
  let text = value;
  if (field) {
    switch (field.type) {
      case 'yesno': text = value === '1' ? t('fieldInput.yes') : value === '0' ? t('fieldInput.no') : value; break;
      case 'vocab': text = vocabLabels(vocabulary, [value], lang, field)[0]; break;
      case 'tags': {
        const entry = facet && facet.values && facet.values.find((v) => v.value === value || v.value === tagKey(value));
        text = (entry && entry.label) || value;
        break;
      }
      default: text = String(selectLabel(field, value, lang));
    }
  }
  return <Chip label={field ? field.name : fieldKey} value={text} onRemove={onRemove} />;
}

/** "90–120 min", "from 1970", "up to 2024-03". */
const rangeText = (t, range, unit) => {
  const suffix = unit ? ` ${unit}` : '';
  if (range.min !== null && range.max !== null) return t('collection.filters.rangeBetween', { min: range.min, max: range.max }) + suffix;
  if (range.min !== null) return t('collection.filters.rangeFrom', { min: range.min }) + suffix;
  return t('collection.filters.rangeTo', { max: range.max }) + suffix;
};

/**
 * One removable chip per active filter (phase 3 §3.1: "Director: Stanley
 * Kubrick"), in the order of the address. Labels come from the types, the
 * places and the facets' answer (a tag's spelling); a value not known yet
 * shows as written.
 */
function FilterChips({ filters, facets, fieldsByKey, places, personal, onRemove }) {
  const { t } = useTranslation();
  const facetOf = (key) => (facets || []).find((facet) => facet.key === key);
  const chips = [];
  Object.entries(filters.values).forEach(([key, values]) => values.forEach((value) => chips.push(
    <ValueChip key={`v:${key}:${value}`} field={fieldsByKey[key]} fieldKey={key} value={value} facet={facetOf(key)}
               onRemove={() => onRemove({ kind: 'value', key, value })} />,
  )));
  Object.entries(filters.ranges).forEach(([key, range]) => {
    const field = fieldsByKey[key];
    const unit = field && field.type === 'measure' ? (field.units || [])[0] : field && field.type === 'money' ? field.currency || 'EUR' : '';
    chips.push(
      <Chip key={`r:${key}`} label={key === YEAR ? t('collection.filters.year') : (field ? field.name : key)} value={rangeText(t, range, unit)}
            onRemove={() => onRemove({ kind: 'range', key })} />,
    );
  });
  filters.places.forEach((value) => {
    const place = (places || []).find((p) => String(p.id) === value);
    chips.push(
      <Chip key={`p:${value}`} label={t('collection.filters.place')}
            value={value === NO_PLACE ? t('collection.filters.noPlace') : place ? pathLabel(t, place.path) : '…'}
            onRemove={() => onRemove({ kind: 'place', value })} />,
    );
  });
  const verbs = personal.verbs || [];
  const verb = verbs.length === 1 ? verbs[0] : 'mixed';
  if (filters.done !== null) {
    chips.push(<Chip key="done" value={t(`collection.filters.${filters.done === '1' ? 'doneYes' : 'doneNo'}.${verb}`)}
                     onRemove={() => onRemove({ kind: 'flag', key: 'done' })} />);
  }
  if (filters.rated !== null) {
    chips.push(<Chip key="rated" value={t(filters.rated === '1' ? 'collection.filters.ratedYes' : 'collection.filters.ratedNo')}
                     onRemove={() => onRemove({ kind: 'flag', key: 'rated' })} />);
  }
  if (filters.avg !== null) {
    chips.push(<Chip key="avg" label={t('collection.filters.average')} value={t('collection.filters.atLeast', { count: Number(filters.avg) })}
                     onRemove={() => onRemove({ kind: 'flag', key: 'avg' })} />);
  }
  return chips;
}

export default FilterChips;
